import { useCallback, useState, useMemo } from "react";
import isFunction from "../utils/isFunction";

const useCounter = ({
	start = 0,
	min = 0,
	max = Infinity,
	resetOnMax = false,
	disabled = false,
} = {}) => {
	const [counter, setCounter] = useState(start);

	const incrementCounter = useCallback(
		(counter) => Math.min(max, counter + 1) % (resetOnMax ? max : Infinity),
		[max, resetOnMax]
	);
	const increment = useCallback(
		() => (disabled ? null : setCounter(incrementCounter)),
		[incrementCounter, disabled]
	);

	const decrementCounter = useCallback(
		(counter) => Math.max(min, counter - 1),
		[min]
	);
	const decrement = useCallback(
		() => (disabled ? null : setCounter(decrementCounter)),
		[decrementCounter, disabled]
	);

	const nextCounter = useMemo(
		() => incrementCounter(counter),
		[incrementCounter, counter]
	);
	const prevCounter = useMemo(
		() => decrementCounter(counter),
		[decrementCounter, counter]
	);

	const isMin = useMemo(() => counter === min, [min, counter]);
	const isMax = useMemo(() => counter === max, [max, counter]);

	const procValue = useCallback(
		(value) => Math.max(min, Math.min(max, value)),
		[max, min]
	);

	const handleCounter = useCallback(
		(cb) => {
			if (disabled) return;
			setCounter((counter) =>
				procValue(isFunction(cb) ? cb(counter) : cb)
			);
		},
		[procValue, disabled]
	);

	return {
		counter,
		setCounter: handleCounter,
		increment,
		decrement,
		nextCounter,
		prevCounter,
		isMin,
		isMax,
	};
};

export default useCounter;
